import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/apiClient';

/** Zeitbudget fuer eine KI-Beleganalyse (Backend braucht 20-60 s). */
export const ANALYSE_TIMEOUT_MS = 180000;

interface LinkedBooking {
  buchung_id: number;
  verwendungszweck: string;
}

export interface Document {
  id: number;
  name: string;
  /** Optionale, eindeutige Belegnummer (steht auf dem Original / der Klarsichtfolie). */
  beleg_nr?: string | null;
  file_path: string;
  created_at: string;
  member_id: string;
  member_name?: string;
  size: number;
  tag?: string;
  ai_comment?: string;
  invoice_amount?: string;
  invoice_date?: string;
  document_type?: string;
  isNew?: boolean;
  is_linked?: number;
  is_bank_statement?: number;
  linked_bookings?: LinkedBooking[];
  linked_drinks_events?: Array<{
    id: number;
    beschriftung: string;
    abrechnungsmonat: string;
  }>;
}

export interface DocumentUploadResult {
  uploaded: Document[];
  errors: { file: string; error: string }[];
}

interface Tag {
  id: number;
  tag: string;
  count?: number;
}

const useDocumentManagement = (memberId = 'unknown') => {
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadRequest = useRef(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocs, setFilteredDocs] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [newTag, setNewTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [isAskingAI, setIsAskingAI] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [pdfError, setPdfError] = useState<string | null>(null);
  
  // Extended filter states
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<'all' | 'pdf' | 'image'>('all');
  const [filterUploader, setFilterUploader] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState({ start: '', end: '' });
  const [filterSize, setFilterSize] = useState({ min: '', max: '' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);


  useEffect(() => {
    let filtered = documents.filter((doc) => {
      // Tag filter
      if (filterTags.length > 0) {
        if (!doc.tag || !filterTags.every((tag) => doc.tag?.split(',').includes(tag))) {
          return false;
        }
      }
      
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          doc.name.toLowerCase().includes(searchLower) ||
          (doc.beleg_nr && doc.beleg_nr.toLowerCase().includes(searchLower)) ||
          (doc.tag && doc.tag.toLowerCase().includes(searchLower)) ||
          (doc.ai_comment && doc.ai_comment.toLowerCase().includes(searchLower)) ||
          (doc.member_name && doc.member_name.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      
      // Type filter
      if (filterType !== 'all') {
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.file_path);
        const isPdf = /\.pdf$/i.test(doc.file_path);
        if (filterType === 'image' && !isImage) return false;
        if (filterType === 'pdf' && !isPdf) return false;
      }
      
      // Uploader filter
      if (filterUploader !== 'all' && doc.member_name !== filterUploader) {
        return false;
      }
      
      // Date range filter
      if (filterDateRange.start || filterDateRange.end) {
        const docDate = new Date(doc.created_at);
        if (filterDateRange.start && docDate < new Date(filterDateRange.start)) return false;
        if (filterDateRange.end && docDate > new Date(filterDateRange.end)) return false;
      }
      
      // Size filter (in KB)
      if (filterSize.min || filterSize.max) {
        const docSize = doc.size || 0;
        const minSize = filterSize.min ? parseFloat(filterSize.min) * 1024 : 0;
        const maxSize = filterSize.max ? parseFloat(filterSize.max) * 1024 : Infinity;
        if (docSize < minSize || docSize > maxSize) return false;
      }
      
      return true;
    });
    
    // Sort documents
    filtered = filtered.sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'size':
          aVal = a.size || 0;
          bVal = b.size || 0;
          break;
        case 'uploader':
          aVal = a.member_name || '';
          bVal = b.member_name || '';
          break;
        case 'type':
          aVal = a.file_path.split('.').pop() || '';
          bVal = b.file_path.split('.').pop() || '';
          break;
        default: // 'created_at'
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    setFilteredDocs(filtered);
  }, [documents, searchTerm, filterTags, sortBy, sortOrder, filterType, filterUploader, filterDateRange, filterSize]);

  const fetchDocuments = useCallback(async () => {
    const request = ++loadRequest.current;
    setIsLoadingDocuments(true);
    setLoadError(null);
    try {
      const response = await apiClient.get('/api/documents');
      if (!Array.isArray(response.data)) throw new Error('Ungültige Dokumentenliste.');
      if (request !== loadRequest.current) return;
      setDocuments([...response.data].sort((a: Document, b: Document) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));
    } catch (err: any) {
      if (request === loadRequest.current) setLoadError(err?.response?.data?.error || 'Dokumente konnten nicht geladen werden. Bitte erneut versuchen.');
    } finally {
      if (request === loadRequest.current) setIsLoadingDocuments(false);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/tags');
      const data: Tag[] = response.data || [];
      setAvailableTags(data.map((t) => t.tag));
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, []);

  useEffect(() => {
    void fetchDocuments();
    void fetchTags();
    return () => { loadRequest.current += 1; };
  }, [fetchDocuments, fetchTags]);

  const handleFileUpload = async (files: FileList | File[]): Promise<DocumentUploadResult> => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('documents', file);
      formData.append('names[]', file.name);
    });
    formData.append('member_id', memberId);
    try {
      const response = await apiClient.post('/api/documents/upload', formData, { timeout: 180000 });
      const data = response.data;
      const uploaded = Array.isArray(data) ? data : data?.uploaded;
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      if (!Array.isArray(uploaded) || uploaded.some(doc => !doc || !Number.isInteger(Number(doc.id)) || typeof doc.file_path !== 'string' || typeof doc.name !== 'string')) {
        throw new Error('Der Server hat ein ungültiges Upload-Ergebnis geliefert. Bitte Dokumentenliste neu laden.');
      }
      setDocuments(prev => [...uploaded, ...prev.filter(doc => !uploaded.some(next => Number(next.id) === Number(doc.id)))]);
      // Refresh immediately: no timer may overwrite a newer upload or filter state.
      await fetchDocuments();
      return { uploaded, errors };
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Fehler beim Hochladen';
      setPdfError(message);
      throw new Error(message);
    }
  };

  const handleAddTag = async (docId: number, tag: string) => {
    try {
      await apiClient.put(`/api/documents/${docId}`, { 
        tag: tag.trim() 
      });
      await fetchDocuments();
      await fetchTags();
    } catch (err) {
      console.error('Error adding tag:', err);
    }
  };

  const handleDeleteTag = async (docId: number, tagToDelete: string) => {
    try {
      const doc = documents.find(d => d.id === docId);
      if (!doc) return;
      
      const currentTags = doc.tag ? doc.tag.split(',') : [];
      const newTags = currentTags.filter(t => t !== tagToDelete);
      
      await apiClient.put(`/api/documents/${docId}`, { 
        tag: newTags.join(',') 
      });
      await fetchDocuments();
      await fetchTags();
    } catch (err) {
      console.error('Error deleting tag:', err);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    try {
      await apiClient.delete(`/api/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      setPdfError('🗑️ Dokument gelöscht');
      setTimeout(() => setPdfError(null), 3000);
    } catch (err) {
      console.error('Error deleting document:', err);
      setPdfError('❌ Fehler beim Löschen');
    }
  };

  const handleDeleteMultiple = async (docIds: number[]) => {
    const results = await Promise.allSettled(docIds.map(id => apiClient.delete(`/api/documents/${id}`)));
    const deletedIds = docIds.filter((_, index) => results[index].status === 'fulfilled');
    const failedIds = docIds.filter((_, index) => results[index].status === 'rejected');
    setDocuments(prev => prev.filter(doc => !deletedIds.includes(doc.id)));
    setSelectedDocs(failedIds);
    setPdfError(failedIds.length
      ? `${deletedIds.length} Dokumente gelöscht; ${failedIds.length} konnten nicht gelöscht werden. Bitte erneut versuchen.`
      : `${deletedIds.length} Dokumente gelöscht.`);
    return { deletedIds, failedIds };
  };

  const handleMergeDocuments = async (docIds: number[]) => {
    if (docIds.length < 2) {
      setPdfError('Mindestens 2 Dokumente auswählen');
      return;
    }

    try {
      const response = await apiClient.post('/api/documents/merge-documents', { 
        document_ids: docIds 
      });
      const newDoc = response.data;
      
      if (newDoc.success && newDoc.docId) {
        await fetchDocuments();
        setSelectedDocs([]);
        setPdfError('✅ Dokumente zusammengeführt');
        setTimeout(() => setPdfError(null), 3000);
        return newDoc.docId;
      }
    } catch (err) {
      console.error('Error merging documents:', err);
      setPdfError('❌ Fehler beim Zusammenführen');
    }
  };

  const sendToAI = async (docId: number) => {
    setIsAskingAI(true);
    try {
      // Correct API endpoint based on backend documentation
      // Die Analyse dauert je nach Beleg 20-60 s; der Client-Standard von 30 s
      // reicht nicht (sonst "fehlgeschlagen", obwohl das Backend fertig wird).
      const response = await apiClient.post(
        '/api/documents/ask-ai',
        { document_id: docId },
        { timeout: ANALYSE_TIMEOUT_MS },
      );
      
      // Check if response indicates success
      if (response.data && response.data.success) {
        const { description, tags, suggestedName, invoice_amount, invoice_date, confidence } = response.data;

        // Nur Tags direkt übernehmen — sie sind reine Zuordnung und nicht sichtbar
        // umbenennend. Name, Betrag und Belegdatum werden NICHT still geschrieben,
        // sondern im KI-Modal zur Bestätigung vorgelegt (die Beschreibung speichert
        // die Route selbst in ai_comment).
        const updatedTags = tags ? tags.join(',') : undefined;
        if (updatedTags) {
          await apiClient.put(`/api/documents/${docId}`, { tag: updatedTags });
        }

        await fetchDocuments();
        return {
          success: true,
          result: { description, suggestedName, invoice_amount, invoice_date, confidence },
        };
      } else {
        // Backend returned an error
        const errorMessage = response.data?.message || response.data?.error || 'AI-Analyse fehlgeschlagen';
        console.error('AI analysis failed:', errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (err: any) {
      console.error('Error with AI analysis:', err);
      // Extract error message from response or use default
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'AI-Analyse fehlgeschlagen';
      return { success: false, message: errorMessage };
    } finally {
      setIsAskingAI(false);
    }
  };

  const downloadDocument = async (docId: number, fileName: string) => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get(`/api/documents/official-copy/${docId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const responseType = response.data.type || response.headers['content-type'] || '';
      const extension = responseType.includes('application/pdf') ? '.pdf'
        : responseType.includes('image/png') ? '.png'
        : '';
      const currentExtension = fileName.match(/\.[a-z0-9]+$/i)?.[0] || '';
      const baseName = currentExtension ? fileName.slice(0, -currentExtension.length) : fileName;
      link.download = `${baseName}_Dokumentkopie${extension || currentExtension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      setPdfError('❌ Download fehlgeschlagen');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFilterTagToggle = (tag: string) => {
    setFilterTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleRemoveFilterTag = (tag: string) => {
    setFilterTags(prev => prev.filter(t => t !== tag));
  };

  // Utility functions
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
  };

  const getUploaderName = (doc: Document) => {
    return doc.member_name || 'Unbekannt';
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getTagCount = (tag: string) => {
    return documents.filter(doc => 
      doc.tag && doc.tag.split(',').includes(tag)
    ).length;
  };

  return {
    isLoadingDocuments,
    loadError,
    documents,
    setDocuments,
    filteredDocs,
    selectedDoc,
    setSelectedDoc,
    newTag,
    setNewTag,
    searchTerm,
    setSearchTerm,
    isDownloading,
    availableTags,
    isAskingAI,
    selectedDocs,
    setSelectedDocs,
    filterTags,
    pdfError,
    setPdfError,
    // Filter states
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    filterUploader,
    setFilterUploader,
    filterDateRange,
    setFilterDateRange,
    filterSize,
    setFilterSize,
    showAdvancedFilters,
    setShowAdvancedFilters,
    // Functions
    handleFileUpload,
    handleAddTag,
    handleDeleteTag,
    handleDeleteDocument,
    handleDeleteMultiple,
    handleMergeDocuments,
    handleFilterTagToggle,
    handleRemoveFilterTag,
    sendToAI,
    downloadDocument,
    formatSize,
    getUploaderName,
    truncateText,
    getTagCount,
    fetchDocuments,
    fetchTags,
  };
};

export default useDocumentManagement;
